/**
 * Event Style Configuration Service
 * Generates UI styling for discipline event types
 * Separates visual presentation from business logic
 */

import { consequenceConfig } from './consequenceConfig.js';
import { getUIColors } from '../../../utils/colorUtils.js';

/**
 * Theme-based style configurations
 * Each theme defines Tailwind classes for different event types
 */
const STYLE_THEMES = {
  gradient: {
    demerit: {
      color: 'text-amber-600',
      bgColor: 'bg-gradient-to-br from-amber-50 to-yellow-50',
      borderColor: 'border-amber-600',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600'
    },
    detention: {
      color: 'text-orange-600',
      bgColor: 'bg-gradient-to-br from-orange-50 to-red-50',
      borderColor: 'border-orange-500',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600'
    },
    'friday-detention': {
      color: 'text-indigo-600',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-blue-50',
      borderColor: 'border-indigo-500',
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600'
    },
    'saturday-detention': {
      color: 'text-purple-600',
      bgColor: 'bg-gradient-to-br from-purple-50 to-violet-50',
      borderColor: 'border-purple-500',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600'
    },
    'homework-detention': {
      color: 'text-pink-600',
      bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50',
      borderColor: 'border-pink-500',
      iconBg: 'bg-pink-100',
      iconColor: 'text-pink-600'
    },
    suspension: {
      color: 'text-red-600',
      bgColor: 'bg-gradient-to-br from-red-50 to-rose-50',
      borderColor: 'border-red-500',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600'
    },
    'privilege-loss': {
      color: 'text-slate-600',
      bgColor: 'bg-gradient-to-br from-slate-50 to-gray-50',
      borderColor: 'border-slate-500',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600'
    },
    referral: {
      color: 'text-emerald-600',
      bgColor: 'bg-gradient-to-br from-emerald-50 to-teal-50',
      borderColor: 'border-emerald-500',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600'
    },
    warning: {
      color: 'text-yellow-600',
      bgColor: 'bg-gradient-to-br from-yellow-50 to-amber-50',
      borderColor: 'border-yellow-500',
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600'
    },
    contract: {
      color: 'text-teal-600',
      bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50',
      borderColor: 'border-teal-500',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600'
    },
    restorative: {
      color: 'text-green-600',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
      borderColor: 'border-green-500',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600'
    }
  }
};

/**
 * Special event types that aren't consequences
 */
const SPECIAL_EVENT_STYLES = {
  mention: {
    label: 'Mention',
    color: 'text-blue-600',
    bgColor: 'bg-gradient-to-br from-blue-50 to-indigo-50',
    borderColor: 'border-blue-500',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    icon: '⚠️'
  },
  notification: {
    label: 'Notification',
    color: 'text-cyan-600',
    bgColor: 'bg-gradient-to-br from-cyan-50 to-blue-50',
    borderColor: 'border-cyan-500',
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
    icon: '🔔'
  },
  'contract-consequence': {
    label: 'Contract Alternative',
    color: 'text-teal-600',
    bgColor: 'bg-gradient-to-br from-teal-50 to-green-50',
    borderColor: 'border-teal-500',
    iconBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    icon: '📄'
  },
  'duplicate-resolution': {
    label: 'Duplicate Resolution',
    color: 'text-gray-600',
    bgColor: 'bg-gradient-to-br from-gray-50 to-slate-50',
    borderColor: 'border-gray-500',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-600',
    icon: '📋'
  },
  'suspension-pending-review': {
    label: 'Suspension (Pending Review)',
    color: 'text-rose-600',
    bgColor: 'bg-gradient-to-br from-rose-50 to-pink-50',
    borderColor: 'border-rose-500',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    icon: '🚫'
  },
  consequence: {
    label: 'Consequence',
    color: 'text-indigo-600',
    bgColor: 'bg-gradient-to-br from-indigo-50 to-purple-50',
    borderColor: 'border-indigo-500',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    icon: '⚠️'
  },
  'mention-rule-consequence': {
    label: 'Rule Consequence',
    color: 'text-violet-600',
    bgColor: 'bg-gradient-to-br from-violet-50 to-indigo-50',
    borderColor: 'border-violet-500',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    icon: '⚖️'
  },
  unknown: {
    label: 'Unknown Type',
    color: 'text-red-700',
    bgColor: 'bg-gradient-to-br from-red-100 to-pink-100',
    borderColor: 'border-red-600',
    iconBg: 'bg-red-200',
    iconColor: 'text-red-700',
    icon: '❓'
  }
};

/**
 * Service for generating event styling configurations
 */
class EventStyleConfigService {
  constructor(schoolId) {
    this.schoolId = schoolId;
    this.defaultTheme = 'gradient';
  }

  /**
   * Get complete event type configuration for UI display
   * Combines consequence types with styling
   * @param {string} theme - Theme name (default: 'gradient')
   * @returns {Promise<Object>} Event type configuration object
   */
  async getEventTypeConfig(theme = this.defaultTheme) {
    const config = {};
    const styles = STYLE_THEMES[theme] || STYLE_THEMES.gradient;

    try {
      // Build config for all consequence types from Firestore
      const consequenceTypes = await consequenceConfig.getAll(this.schoolId);

      for (const type of consequenceTypes) {
        const typeStyles = styles[type.value] || styles.demerit; // Fallback to demerit style

        // Generate dynamic colors based on user-selected color
        let dynamicColors = {};
        if (type.color) {
          const uiColors = getUIColors(type.color);
          dynamicColors = {
            hexColor: type.color,  // Original user-selected color
            borderHexColor: uiColors.card.borderAccent,  // Same color for border
            badgeBgColor: uiColors.badge.background,     // Light variant for badge bg
            badgeBorderColor: uiColors.badge.border,     // Medium variant for badge border
          };
        }

        config[type.value] = {
          label: this.toSingular(type.label),
          icon: type.icon,
          ...typeStyles,
          ...dynamicColors
        };
      }
    } catch (error) {
      console.error('Error loading consequence types for styling:', error);
      console.log('[EventStyleConfig] Using fallback styles due to error:', error.message);
      
      // Try to auto-seed consequence types if they don't exist
      try {
        if (this.schoolId && typeof window !== 'undefined' && window.seedConsequenceTypes) {
          console.log('[EventStyleConfig] Attempting to auto-seed consequence types...');
          await window.seedConsequenceTypes(false, this.schoolId);
          console.log('[EventStyleConfig] Auto-seeding complete, retrying load...');
          
          // Retry loading after seeding
          const consequenceTypes = await consequenceConfig.getAll(this.schoolId);
          console.log('[EventStyleConfig] Loaded consequence types after seeding:', consequenceTypes.length);
          
          for (const type of consequenceTypes) {
            const typeStyles = styles[type.value] || styles.demerit;
            let dynamicColors = {};
            if (type.color) {
              const uiColors = getUIColors(type.color);
              dynamicColors = {
                hexColor: type.color,
                borderHexColor: uiColors.card.borderAccent,
                badgeBgColor: uiColors.badge.background,
                badgeBorderColor: uiColors.badge.border,
              };
            }
            
            config[type.value] = {
              label: this.toSingular(type.label),
              icon: type.icon,
              ...typeStyles,
              ...dynamicColors
            };
          }
        }
      } catch (seedError) {
        console.warn('[EventStyleConfig] Auto-seeding failed:', seedError.message);
      }
    }

    // Ensure essential consequence types are always available
    // This handles cases where they're missing from Firestore but exist in styles
    const essentialTypes = [
      'demerit', 'warning', 'detention', 'homework-detention', 
      'friday-detention', 'saturday-detention', 'suspension',
      'privilege-loss', 'referral', 'contract', 'restorative'
    ];
    for (const type of essentialTypes) {
      if (!config[type] && styles[type]) {
        // Create proper labels based on type
        let label = this.toSingular(type.replace('-', ' '));
        
        // Capitalize first letter of each word for better display
        label = label.split(' ').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        
        config[type] = {
          label,
          icon: this.getDefaultIcon(type),
          ...styles[type]
        };
      }
    }

    // Add special event types
    Object.assign(config, SPECIAL_EVENT_STYLES);

    return config;
  }

  /**
   * Get default icon for consequence type
   * @param {string} type - Consequence type
   * @returns {string} Default icon emoji
   */
  getDefaultIcon(type) {
    const iconMap = {
      'demerit': '⚠️',
      'detention': '🕐',
      'friday-detention': '📅',
      'saturday-detention': '📆',
      'homework-detention': '📝',
      'suspension': '🚫',
      'privilege-loss': '🔒',
      'referral': '📋',
      'warning': '⚠️'
    };
    return iconMap[type] || '⚠️';
  }

  /**
   * Get style for a specific event type
   * @param {string} eventType - Event type value
   * @param {string} theme - Theme name
   * @returns {Promise<Object>} Style configuration
   */
  async getStyleForType(eventType, theme = this.defaultTheme) {
    const config = await this.getEventTypeConfig(theme);
    return config[eventType] || config.unknown;
  }

  /**
   * Convert plural label to singular
   * @param {string} label - Plural label
   * @returns {string} Singular label
   */
  toSingular(label) {
    return label
      .replace(/s$/, '')
      .replace(/Demerits/, 'Demerit')
      .replace(/ies$/, 'y');
   }

   // NOTE: Duplicate getDefaultIcon removed - original is at line ~296

   /**
    * Get available themes
    * @returns {Array<string>} Array of theme names
    */
   getAvailableThemes() {
     return Object.keys(STYLE_THEMES);
   }

  /**
   * Add custom theme
   * @param {string} name - Theme name
   * @param {Object} styles - Style definitions
   */
  addTheme(name, styles) {
    STYLE_THEMES[name] = styles;
  }
}

// Export class for instantiation
export { EventStyleConfigService };

// Export singleton instance for backward compatibility
export const eventStyleConfig = new EventStyleConfigService();

// Default export
export default eventStyleConfig;
